import { useState, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Settings2, Pencil, Eye, Info } from 'lucide-react';
import { useNotificationTemplates, NotificationTemplate } from '@/hooks/useNotificationTemplates';

const TEMPLATE_CATEGORIES = {
  leads: ['lead_new', 'lead_status_change', 'lead_summary_quickscan', 'lead_summary_hbe', 'lead_summary_intake'],
  locations: ['location_new', 'location_won', 'location_lost', 'location_stage_change'],
  projects: ['project_delivered', 'project_status_change', 'project_note_new'],
  todos: ['todo_assigned'],
};

const CATEGORY_LABELS: Record<string, string> = {
  leads: 'Leads & Samenvattingen',
  locations: 'Charging Locaties',
  projects: 'Projecten',
  todos: 'Taken',
};

const TEMPLATE_LABELS: Record<string, string> = {
  lead_new: 'Nieuwe Lead',
  lead_status_change: 'Lead Status Wijziging',
  lead_summary_quickscan: 'Lead Samenvatting (Quickscan)',
  lead_summary_hbe: 'Lead Samenvatting (HBE)',
  lead_summary_intake: 'Lead Samenvatting (Intake)',
  location_new: 'Nieuwe Locatie',
  location_won: 'Locatie Gewonnen',
  location_lost: 'Locatie Verloren',
  location_stage_change: 'Locatie Stage Wijziging',
  project_delivered: 'Project Opgeleverd',
  project_status_change: 'Project Status Wijziging',
  project_note_new: 'Nieuwe Project Notitie',
  todo_assigned: 'Taak Toegewezen',
};

// Example data for preview - updated with more placeholders
const EXAMPLE_DATA: Record<string, Record<string, string>> = {
  lead_new: { source: 'Quickscan', summary: 'Bakkerij De Groot - €2.500/maand energie' },
  lead_status_change: { stage: 'interesse', summary: 'Bakkerij De Groot' },
  lead_summary_quickscan: { 
    company_name: 'Bakkerij De Groot', 
    monthly_energy_costs: '2500', 
    energy_potential_score: '85',
    yearly_savings: '12000',
    co2_reduction: '45',
    roi_months: '24',
    business_type: 'Bakkerij',
    employee_count: '25'
  },
  lead_summary_hbe: { 
    company_name: 'Transport BV', 
    contact_name: 'Jan Jansen',
    phone: '0612345678',
    email: 'jan@transport.nl',
    vehicles_count: '12', 
    total_kwh: '150000',
    total_voordeel: '8400',
    total_hbe: '420',
    zonnestroom_pct: '30',
    netstroom_pct: '70',
    wants_proposal: 'ja' 
  },
  lead_summary_intake: { 
    contact_name: 'Jan Jansen', 
    company_name: 'Logistiek BV',
    branch: 'Logistiek', 
    urgency: 'hoog',
    region: 'Noord-Holland',
    role: 'Eigenaar',
    company_size: '50-100',
    best_time: 'ochtend',
    connection_type: '3x80A',
    phone: '0612345678',
    email: 'jan@logistiek.nl',
    pain_points: 'Hoge energiekosten, Netcongestie',
    flex_options: 'Batterij, Zonnepanelen',
    data_available: 'ja',
    budget_ready: 'ja',
    score: '8'
  },
  location_new: { name: 'Laadplein Amsterdam', city: 'Amsterdam' },
  location_won: { name: 'Laadplein Amsterdam' },
  location_lost: { name: 'Laadplein Rotterdam' },
  location_stage_change: { name: 'Laadplein Utrecht', stage: 'realisatie' },
  project_delivered: { name: 'Batterij Project Eindhoven' },
  project_status_change: { name: 'Solar Project Den Haag', status: 'in uitvoering' },
  project_note_new: { project_name: 'Batterij Project', preview: 'Inspectie gepland voor...' },
  todo_assigned: { title: 'Offerte opstellen', due_date: '15-12-2024' },
};

function replacePlaceholders(template: string, data: Record<string, string>): string {
  let result = template;
  Object.entries(data).forEach(([key, value]) => {
    result = result.replace(new RegExp(`\\{${key}\\}`, 'g'), value);
  });
  return result;
}

export function NotificationTemplateEditor() {
  const { templates, loading, updateTemplate } = useNotificationTemplates();
  const [editingTemplate, setEditingTemplate] = useState<NotificationTemplate | null>(null);
  const [editForm, setEditForm] = useState({
    emoji: '',
    title_template: '',
    body_template: '',
  });
  const [activeField, setActiveField] = useState<'title' | 'body'>('title');
  
  // Refs for input fields to track cursor position
  const titleInputRef = useRef<HTMLInputElement>(null);
  const bodyInputRef = useRef<HTMLTextAreaElement>(null);

  const handleEdit = (template: NotificationTemplate) => {
    setEditingTemplate(template);
    setEditForm({
      emoji: template.emoji || '',
      title_template: template.title_template,
      body_template: template.body_template,
    });
    setActiveField('title');
  };

  const handleSave = async () => {
    if (!editingTemplate) return;
    const success = await updateTemplate(editingTemplate.id, editForm);
    if (success) {
      setEditingTemplate(null);
    }
  };

  const handleToggleActive = async (template: NotificationTemplate) => {
    await updateTemplate(template.id, { is_active: !template.is_active });
  };

  const insertPlaceholder = (placeholder: string) => {
    const placeholderText = `{${placeholder}}`;
    
    if (activeField === 'title') {
      const input = titleInputRef.current;
      if (input) {
        const start = input.selectionStart || 0;
        const end = input.selectionEnd || 0;
        const newValue = 
          editForm.title_template.substring(0, start) + 
          placeholderText + 
          editForm.title_template.substring(end);
        setEditForm({ ...editForm, title_template: newValue });
        
        // Restore cursor position after React re-render
        setTimeout(() => {
          input.focus();
          input.setSelectionRange(start + placeholderText.length, start + placeholderText.length);
        }, 0);
      }
    } else {
      const textarea = bodyInputRef.current;
      if (textarea) {
        const start = textarea.selectionStart || 0;
        const end = textarea.selectionEnd || 0;
        const newValue = 
          editForm.body_template.substring(0, start) + 
          placeholderText + 
          editForm.body_template.substring(end);
        setEditForm({ ...editForm, body_template: newValue });
        
        setTimeout(() => {
          textarea.focus();
          textarea.setSelectionRange(start + placeholderText.length, start + placeholderText.length);
        }, 0);
      }
    }
  };

  const getPreview = (template: NotificationTemplate, form?: typeof editForm) => {
    const data = EXAMPLE_DATA[template.template_key] || {};
    const titleTemplate = form?.title_template || template.title_template;
    const bodyTemplate = form?.body_template || template.body_template;
    const emoji = form?.emoji ?? template.emoji;
    
    return {
      title: (emoji ? emoji + ' ' : '') + replacePlaceholders(titleTemplate, data),
      body: replacePlaceholders(bodyTemplate, data),
    };
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64" />
        </CardHeader>
        <CardContent className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
            <Settings2 className="h-5 w-5" />
            Notificatie Templates
          </CardTitle>
          <CardDescription>
            Pas de tekst en emoji's van notificaties en lead samenvattingen aan
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Info banner */}
          <div className="flex items-start gap-2 p-3 bg-muted/50 rounded-lg text-sm">
            <Info className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
            <p className="text-muted-foreground">
              Gebruik <code className="bg-muted px-1 rounded">{'{placeholder}'}</code> voor dynamische waarden. 
              Wijzigingen worden direct toegepast op nieuwe notificaties.
            </p>
          </div>

          {Object.entries(TEMPLATE_CATEGORIES).map(([category, keys]) => {
            const categoryTemplates = templates.filter((t) => keys.includes(t.template_key));
            if (categoryTemplates.length === 0) return null;

            return (
              <div key={category} className="space-y-3">
                <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                  {CATEGORY_LABELS[category]}
                </h3>
                <div className="space-y-2">
                  {categoryTemplates.map((template) => {
                    const preview = getPreview(template);
                    const isSummaryTemplate = template.template_key.startsWith('lead_summary');

                    return (
                      <div
                        key={template.id}
                        className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 border rounded-lg bg-card"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-sm">
                              {TEMPLATE_LABELS[template.template_key] || template.template_key}
                            </span>
                            {!template.is_active && (
                              <Badge variant="secondary" className="text-xs">Uitgeschakeld</Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-1 truncate">
                            {isSummaryTemplate ? (
                              <span className="italic">Format: {preview.title || preview.body}</span>
                            ) : (
                              <>
                                <Eye className="inline h-3 w-3 mr-1" />
                                {preview.title}
                              </>
                            )}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 self-end sm:self-center">
                          <Switch
                            checked={template.is_active}
                            onCheckedChange={() => handleToggleActive(template)}
                            className="scale-90"
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(template)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={!!editingTemplate} onOpenChange={(open) => !open && setEditingTemplate(null)}>
        <DialogContent className="max-w-[95vw] sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingTemplate && (TEMPLATE_LABELS[editingTemplate.template_key] || editingTemplate.template_key)}
            </DialogTitle>
          </DialogHeader>

          {editingTemplate && (
            <div className="space-y-4">
              {/* Description */}
              {editingTemplate.description && (
                <p className="text-sm text-muted-foreground">{editingTemplate.description}</p>
              )}

              {/* Clickable Placeholders */}
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">
                  Klik op een placeholder om in te voegen:
                </Label>
                <div className="flex flex-wrap gap-1">
                  {editingTemplate.available_placeholders?.map((p) => (
                    <Badge 
                      key={p} 
                      variant="outline" 
                      className="text-xs font-mono cursor-pointer hover:bg-primary/10 hover:border-primary transition-colors"
                      onClick={() => insertPlaceholder(p)}
                    >
                      {'{' + p + '}'}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Emoji (only for notification templates, not summaries) */}
              {!editingTemplate.template_key.startsWith('lead_summary') && (
                <div className="space-y-2">
                  <Label>Emoji</Label>
                  <Input
                    value={editForm.emoji}
                    onChange={(e) => setEditForm({ ...editForm, emoji: e.target.value })}
                    placeholder="🎯"
                    className="w-20"
                  />
                </div>
              )}

              {/* Title */}
              <div className="space-y-2">
                <Label>
                  {editingTemplate.template_key.startsWith('lead_summary') ? 'Samenvatting Format' : 'Titel'}
                </Label>
                <Input
                  ref={titleInputRef}
                  value={editForm.title_template}
                  onChange={(e) => setEditForm({ ...editForm, title_template: e.target.value })}
                  onFocus={() => setActiveField('title')}
                  placeholder="Template..."
                  className={activeField === 'title' ? 'ring-2 ring-primary' : ''}
                />
              </div>

              {/* Body (only for notification templates) */}
              {!editingTemplate.template_key.startsWith('lead_summary') && (
                <div className="space-y-2">
                  <Label>Beschrijving</Label>
                  <Textarea
                    ref={bodyInputRef}
                    value={editForm.body_template}
                    onChange={(e) => setEditForm({ ...editForm, body_template: e.target.value })}
                    onFocus={() => setActiveField('body')}
                    placeholder="Template..."
                    rows={2}
                    className={activeField === 'body' ? 'ring-2 ring-primary' : ''}
                  />
                </div>
              )}

              {/* Live Preview */}
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Live Preview:</Label>
                <div className="p-3 bg-muted rounded-lg text-sm">
                  {editingTemplate.template_key.startsWith('lead_summary') ? (
                    <p className="font-medium">
                      {replacePlaceholders(editForm.title_template, EXAMPLE_DATA[editingTemplate.template_key] || {})}
                    </p>
                  ) : (
                    <>
                      <p className="font-medium">
                        {(editForm.emoji ? editForm.emoji + ' ' : '') +
                          replacePlaceholders(editForm.title_template, EXAMPLE_DATA[editingTemplate.template_key] || {})}
                      </p>
                      <p className="text-muted-foreground mt-1">
                        {replacePlaceholders(editForm.body_template, EXAMPLE_DATA[editingTemplate.template_key] || {})}
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEditingTemplate(null)}>
              Annuleren
            </Button>
            <Button onClick={handleSave}>Opslaan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
